import { yupResolver } from '@hookform/resolvers/yup';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Stack,
  Toolbar,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import PhoneFieldElement from 'core/components/PhoneField/PhoneFieldElement';
import { Can } from 'core/components/permissions/Can';
import { phoneValidator } from 'core/utils/validators/common';
import useCurrentUser from 'hooks/swr/useCurrentUser';
import CardedPage from 'layout/pages/CardedPage';
import { isEmpty, isNil, startCase } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useContext } from 'react';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import { useNavigate } from 'react-router-dom';
import { updateShopAsync } from 'services/shopService';
import StoreContext from 'stores/context/StoreContext';
import { object, string, boolean } from 'yup';
import ProfileEditPageHeader from './ProfileEditPageHeader';
import ProfilePageSkeleton from './ProfilePageSkeleton';
import type { FC } from 'react';

interface ProfileEditFormData {
  name: string;
  surname: string;
  patronymic: string;
  phone: string;
  shopName: string;
  shopDescription: string;
  showOrdersWithoutActionDateInCalendar: boolean;
}

const title = 'Редактирование профиля';

const ProfileEditPage: FC = observer(() => {
  const { notifyStore, authStore, shopStore, userStore } = useContext(StoreContext);
  const { data: user, isLoading, mutate } = useCurrentUser();
  const navigate = useNavigate();

  const schema = object<ProfileEditFormData>({
    surname: string()
      .required()
      .max(300)
      .transform((v) => (isEmpty(v) ? '' : startCase(v))),
    name: string()
      .required()
      .max(300)
      .transform((v) => (isEmpty(v) ? '' : startCase(v))),
    patronymic: string()
      .max(300)
      .transform((v) => (isEmpty(v) ? '' : startCase(v))),
    phone: phoneValidator.required(),
    shopName: string().required().max(300),
    shopDescription: string().max(1000),
    showOrdersWithoutActionDateInCalendar: boolean(),
  }).required();

  const formContext = useForm<ProfileEditFormData>({
    mode: 'onTouched',
    values: {
      phone: user?.phone ?? '',
      surname: user?.surname ?? '',
      name: user?.name ?? '',
      patronymic: user?.patronymic ?? '',
      shopName: shopStore.shop?.name ?? '',
      shopDescription: shopStore.shop?.description ?? '',
      showOrdersWithoutActionDateInCalendar:
        shopStore.shop?.settings?.showOrdersWithoutActionDateInCalendar ?? false,
    },
    resolver: yupResolver(schema),
  });

  const { isSubmitting } = formContext.formState;

  const cancelEditing = (): void => {
    navigate('/profile');
  };

  const saveProfile = async (data: ProfileEditFormData): Promise<void> => {
    try {
      await authStore.updateCurrentUser({
        name: data.name,
        surname: data.surname,
        patronymic: data.patronymic,
        phone: data.phone,
        mangoExtension: user?.mangoExtension ?? '',
      });

      if (userStore.userData && shopStore.shop && shopStore.shop.settings) {
        await updateShopAsync(shopStore.shop.id, {
          name: data.shopName,
          description: data.shopDescription,
          settings: {
            showOrdersWithoutActionDateInCalendar:
              data.showOrdersWithoutActionDateInCalendar,
            enabledModules: shopStore.shop.settings.enabledModules,
          },
          mangoVpbxApiKey: shopStore.shop.mangoVpbxApiKey,
          mangoVpbxApiSalt: shopStore.shop.mangoVpbxApiSalt,
        });

        shopStore.updateShopInfo();
      }

      await mutate();

      notifyStore.showNotify('Изменения сохранены', 'success');
      navigate('/profile');
    } catch (err) {
      console.error('Failed to update profile:', err);
      notifyStore.showNotify(
        'Не удалось сохранить изменения. Попробуйте позже.',
        'error'
      );
    }
  };

  if (isLoading || isNil(user)) {
    return (
      <CardedPage title={title}>
        <ProfilePageSkeleton />
      </CardedPage>
    );
  }

  return (
    <CardedPage title={title} header={<ProfileEditPageHeader user={user} />}>
      <FormContainer
        FormProps={{ style: { maxWidth: '600px' } }}
        formContext={formContext}
        onSuccess={saveProfile}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Личная информация
        </Typography>

        <TextFieldElement
          label='Фамилия'
          margin='normal'
          fullWidth
          name='surname'
          required
        />
        <TextFieldElement label='Имя' margin='normal' fullWidth name='name' required />
        <TextFieldElement label='Отчество' margin='normal' fullWidth name='patronymic' />
        <PhoneFieldElement
          label='Телефон'
          name='phone'
          margin='normal'
          fullWidth
          required
        />

        <Can I='update' a='Shop'>
          <Divider sx={{ my: 3 }} />
          <Typography variant='h6' sx={{ mb: 2 }}>
            Информация об организации
          </Typography>

          <TextFieldElement
            label='Название'
            margin='normal'
            fullWidth
            name='shopName'
            required
          />
          <TextFieldElement
            label='Описание'
            margin='normal'
            fullWidth
            name='shopDescription'
            multiline
            rows={3}
          />

          <FormControlLabel
            control={
              <Switch
                {...formContext.register('showOrdersWithoutActionDateInCalendar')}
                name='showOrdersWithoutActionDateInCalendar'
                checked={formContext.watch('showOrdersWithoutActionDateInCalendar')}
              />
            }
            label='Показывать заказы без даты этапа в календаре'
            sx={{ mt: 2 }}
          />
        </Can>

        <Toolbar disableGutters sx={{ mt: 2 }}>
          <Stack spacing={1} direction='row'>
            <LoadingButton
              loading={isSubmitting}
              loadingPosition='start'
              variant='contained'
              type='submit'
              startIcon={<SaveIcon />}
              disabled={isSubmitting}>
              <span>Сохранить</span>
            </LoadingButton>
            <Button
              disabled={isSubmitting}
              variant='outlined'
              onClick={cancelEditing}
              color='secondary'>
              Отмена
            </Button>
          </Stack>
        </Toolbar>
      </FormContainer>
    </CardedPage>
  );
});

export default ProfileEditPage;
